import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, Upload, X, CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function RequestQuote() {
  const [searchParams] = useSearchParams();
  const prefillModel = searchParams.get('model') || '';
  const prefillCapacity = searchParams.get('capacity') || '';

  React.useEffect(() => {
    document.title = 'Request a Quote | Laminate Chillers – Industrial Chiller Manufacturer';
  }, []);

  const [formData, setFormData] = useState({
    company_name: '', contact_name: '', email: '', phone: '',
    model: prefillModel, required_capacity: prefillCapacity ? `${prefillCapacity} kW` : '',
    leaving_water_temp: '', entering_water_temp: '',
    cooling_tower_data: '', electrical_supply: '',
    site_location: '', additional_notes: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rfqId, setRfqId] = useState('');

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      if (f.size > 25 * 1024 * 1024) { toast.error('File size must be under 25 MB'); return; }
      setFile(f);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error('Please fill in required fields'); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/rfq`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRfqId(res.data.rfq_id);
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="page-transition">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: 'rgba(14,138,138,0.1)' }}>
            <CheckCircle className="w-8 h-8" style={{ color: 'var(--brand-teal)' }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Quote Request Submitted</h1>
          <p className="text-muted-foreground mb-2">Your inquiry reference: <span className="font-mono font-semibold" style={{ color: 'var(--brand-teal)' }}>{rfqId.slice(0, 8)}</span></p>
          <p className="text-sm text-muted-foreground mb-8">Our engineering team will review your requirements and respond within 24\u201348 business hours.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/products"><Button variant="outline">Browse Products</Button></Link>
            <Link to="/"><Button className="text-white" style={{ background: 'var(--brand-teal)' }}>Back to Home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition">
      <section className="py-10 sm:py-14" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link to="/" className="hover:text-[var(--brand-navy)]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span>Request a Quote</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Request a Quote</h1>
          <p className="text-muted-foreground mt-1">Tell us about your cooling requirements and we'll provide a tailored proposal.</p>
        </div>
      </section>

      <section className="py-8" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <form onSubmit={handleSubmit}>
            {/* Contact Info */}
            <Card className="mb-6" style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-6">
                <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>1</span>
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="company_name">Company Name *</Label><Input id="company_name" data-testid="rfq-company-name" value={formData.company_name} onChange={(e) => handleChange('company_name', e.target.value)} required /></div>
                  <div><Label htmlFor="contact_name">Contact Name *</Label><Input id="contact_name" data-testid="rfq-contact-name" value={formData.contact_name} onChange={(e) => handleChange('contact_name', e.target.value)} required /></div>
                  <div><Label htmlFor="email">Email *</Label><Input id="email" type="email" data-testid="rfq-email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required /></div>
                  <div><Label htmlFor="phone">Phone</Label><Input id="phone" data-testid="rfq-phone" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} /></div>
                </div>
              </CardContent>
            </Card>

            {/* Technical Requirements */}
            <Card className="mb-6" style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-6">
                <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>2</span>
                  Technical Requirements
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="model">Model of Interest</Label><Input id="model" data-testid="rfq-model" value={formData.model} onChange={(e) => handleChange('model', e.target.value)} placeholder="e.g., LX-S200" /></div>
                  <div><Label htmlFor="required_capacity">Required Capacity</Label><Input id="required_capacity" data-testid="rfq-project-capacity-input" value={formData.required_capacity} onChange={(e) => handleChange('required_capacity', e.target.value)} placeholder="e.g., 200 kW" /></div>
                  <div><Label htmlFor="leaving_water_temp">Desired Leaving Water Temp</Label><Input id="leaving_water_temp" value={formData.leaving_water_temp} onChange={(e) => handleChange('leaving_water_temp', e.target.value)} placeholder="e.g., 7°C" /></div>
                  <div><Label htmlFor="entering_water_temp">Entering Water Temp</Label><Input id="entering_water_temp" value={formData.entering_water_temp} onChange={(e) => handleChange('entering_water_temp', e.target.value)} placeholder="e.g., 12°C" /></div>
                  <div><Label htmlFor="electrical_supply">Electrical Supply</Label>
                    <Select value={formData.electrical_supply} onValueChange={(v) => handleChange('electrical_supply', v)}>
                      <SelectTrigger><SelectValue placeholder="Select voltage" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="380V/3Ph/50Hz">380V / 3Ph / 50Hz</SelectItem>
                        <SelectItem value="415V/3Ph/50Hz">415V / 3Ph / 50Hz</SelectItem>
                        <SelectItem value="440V/3Ph/60Hz">440V / 3Ph / 60Hz</SelectItem>
                        <SelectItem value="480V/3Ph/60Hz">480V / 3Ph / 60Hz</SelectItem>
                        <SelectItem value="other">Other (specify in notes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label htmlFor="site_location">Site Location</Label><Input id="site_location" value={formData.site_location} onChange={(e) => handleChange('site_location', e.target.value)} placeholder="City, Country" /></div>
                </div>
              </CardContent>
            </Card>

            {/* Site & Additional */}
            <Card className="mb-6" style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-6">
                <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>3</span>
                  Site Conditions & Notes
                </h2>
                <div className="space-y-4">
                  <div><Label htmlFor="cooling_tower_data">Cooling Tower / Heat Rejection Data</Label><Input id="cooling_tower_data" value={formData.cooling_tower_data} onChange={(e) => handleChange('cooling_tower_data', e.target.value)} placeholder="e.g., Open loop, 30°C entering" /></div>
                  <div><Label htmlFor="additional_notes">Additional Notes</Label><Textarea id="additional_notes" rows={4} value={formData.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} placeholder="Special requirements, materials, certifications needed..." /></div>
                </div>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card className="mb-6" style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-6">
                <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white" style={{ background: 'var(--brand-teal)' }}>4</span>
                  Upload Documents (Optional)
                </h2>
                <div className="border-2 border-dashed rounded-lg p-6 text-center" style={{ borderColor: 'hsl(var(--border))' }}>
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-5 h-5" style={{ color: 'var(--brand-teal)' }} />
                      <span className="text-sm">{file.name}</span>
                      <button type="button" onClick={() => setFile(null)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Click to upload P&ID, specifications, or drawings</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DWG, DXF, XLS, images \u2022 Max 25 MB</p>
                      <input type="file" data-testid="rfq-file-upload-input" className="hidden" onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.dwg,.dxf,.xlsx,.xls,.png,.jpg,.jpeg" />
                    </label>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" data-testid="rfq-form-submit-button" disabled={submitting}
              className="w-full text-white py-3 text-base" style={{ background: 'var(--brand-teal)' }}>
              {submitting ? 'Submitting...' : 'Submit Quote Request'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
